import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  applyVariantOverrides,
  resolveVariant,
  type ComponentVariant,
  type VisitorContext,
} from "@ease/page-builder/dynamic-content";
import type { CollabUser, CursorPosition } from "@ease/page-builder/collaboration";
import type { DynamicPreviewPreset } from "@/components/pages/dynamic-content-panel";
import { useStylePanel } from "@/components/pages/style-panel-context";
import {
  buildScopedClassName,
  extractBaseStyle,
} from "@/components/pages/style-state-system";
import { heatmapOverlayColor } from "./editor-utils";

type EditorHeatmapContextValue = {
  showHeatmap: boolean;
  lookup: Record<string, number>;
};

export const EditorHeatmapContext = createContext<EditorHeatmapContextValue>({
  showHeatmap: false,
  lookup: {},
});

export const DYNAMIC_PREVIEW_CONTEXTS: Record<DynamicPreviewPreset, VisitorContext> = {
  default: {
    isReturningVisitor: false,
    device: "desktop",
    country: "US",
    language: "en",
    timeOfDay: "afternoon",
    contactStatus: "anonymous",
  },
  "paid-mobile": {
    utmCampaign: "spring_launch",
    utmSource: "facebook",
    utmMedium: "paid-social",
    isReturningVisitor: false,
    device: "mobile",
    country: "US",
    language: "en",
    timeOfDay: "evening",
    contactStatus: "anonymous",
  },
  "france-organic": {
    utmSource: "google",
    utmMedium: "organic",
    isReturningVisitor: false,
    device: "desktop",
    country: "FR",
    language: "fr",
    timeOfDay: "morning",
    contactStatus: "anonymous",
  },
  "known-returning": {
    utmCampaign: "customer_winback",
    utmSource: "email",
    utmMedium: "crm",
    isReturningVisitor: true,
    device: "desktop",
    country: "US",
    language: "en",
    timeOfDay: "morning",
    contactStatus: "known",
  },
};

type EditorDynamicPreviewContextValue = {
  visitorContext: VisitorContext;
};

export const EditorDynamicPreviewContext = createContext<EditorDynamicPreviewContextValue>({
  visitorContext: DYNAMIC_PREVIEW_CONTEXTS.default,
});

type EditorCollabPreviewContextValue = {
  collaborators: CollabUser[];
  sendCursor: (cursor: CursorPosition) => void;
};

export const EditorCollabPreviewContext = createContext<EditorCollabPreviewContextValue>({
  collaborators: [],
  sendCursor: () => undefined,
});

type EditorCommentPreviewContextValue = {
  isCommentMode: boolean;
  commentPins: Record<string, Array<{
    id: string;
    x: number;
    y: number;
    tone: "open" | "resolved";
    label: string;
  }>>;
  pendingTarget: {
    componentId: string;
    x: number;
    y: number;
  } | null;
  onCommentTarget: (
    componentId: string,
    componentType: string,
    position: { x: number; y: number },
  ) => void;
};

export const EditorCommentPreviewContext = createContext<EditorCommentPreviewContextValue>({
  isCommentMode: false,
  commentPins: {},
  pendingTarget: null,
  onCommentTarget: () => undefined,
});

export function useEditorHeatmap(): EditorHeatmapContextValue {
  return useContext(EditorHeatmapContext);
}

export function applyPreviewVariantProps(
  props: Record<string, unknown>,
  visitorContext: VisitorContext,
): Record<string, unknown> {
  const variants = Array.isArray(props.variants) ? props.variants as ComponentVariant[] : [];
  if (variants.length === 0) {
    return props;
  }

  const matched = resolveVariant(variants, visitorContext);
  const resolved = applyVariantOverrides(props, matched);
  const { variants: _variants, ...cleanProps } = resolved as Record<string, unknown> & { variants?: unknown };
  return cleanProps;
}

export function StylablePreviewShell({
  componentId,
  componentType,
  props,
  children,
}: {
  componentId: string;
  componentType: string;
  props: Record<string, unknown>;
  children: React.ReactNode;
}) {
  const { selectedComponentId, setSelectedComponentId, lockedComponentIds } = useStylePanel();
  const { showHeatmap, lookup } = useEditorHeatmap();
  const { collaborators, sendCursor } = useContext(EditorCollabPreviewContext);
  const { isCommentMode, commentPins, pendingTarget, onCommentTarget } = useContext(EditorCommentPreviewContext);
  const isSelected = selectedComponentId === componentId;
  const isLocked = lockedComponentIds.has(componentId);
  const pins = commentPins[componentId] ?? [];
  const pendingPin = pendingTarget?.componentId === componentId ? pendingTarget : null;
  const blockStyle = extractBaseStyle(props);
  const className = buildScopedClassName(componentId);
  const heat = lookup[componentId];
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [isAnimationVisible, setIsAnimationVisible] = useState(false);
  const heatmapShadow = showHeatmap && typeof heat === "number"
    ? `inset 0 0 0 9999px ${heatmapOverlayColor(heat)}`
    : undefined;
  const outline = isSelected
    ? "2px solid rgba(37, 99, 235, 0.6)"
    : showHeatmap && typeof heat === "number"
      ? `1px solid ${heat > 0.7 ? "rgba(239, 68, 68, 0.45)" : heat > 0.4 ? "rgba(245, 158, 11, 0.45)" : "rgba(14, 165, 233, 0.45)"}`
      : undefined;
  const animation = useMemo(() => extractPreviewAnimation(props), [props]);
  const animatedStyle = useMemo(
    () => buildPreviewAnimationStyle(animation, isAnimationVisible),
    [animation, isAnimationVisible],
  );

  useEffect(() => {
    if (!animation?.entranceType || animation.entranceType === "none") {
      setIsAnimationVisible(true);
      return;
    }

    const node = previewRef.current;
    if (!node) return;

    setIsAnimationVisible(false);
    const threshold = clampThreshold(animation.scrollThreshold);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsAnimationVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [animation]);

  return (
    <div
      ref={previewRef}
      data-block-id={componentId}
      className={className}
      data-ease-anim={animation?.entranceType && animation.entranceType !== "none" ? animation.entranceType : undefined}
      data-ease-threshold={animation?.scrollThreshold ?? undefined}
      onClickCapture={() => {
        if (isCommentMode) {
          setSelectedComponentId(componentId);
          onCommentTarget(componentId, componentType, { x: 0.5, y: 0.5 });
          return;
        }
        if (!isLocked) {
          setSelectedComponentId(componentId);
        }
      }}
      onPointerDownCapture={(event) => {
        if (!isCommentMode) {
          return;
        }

        const bounds = previewRef.current?.getBoundingClientRect();
        if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
          return;
        }

        const x = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
        const y = Math.min(Math.max((event.clientY - bounds.top) / bounds.height, 0), 1);
        setSelectedComponentId(componentId);
        onCommentTarget(componentId, componentType, { x, y });
      }}
      onMouseMove={(event) => {
        const bounds = previewRef.current?.getBoundingClientRect();
        if (!bounds || bounds.height <= 0) {
          return;
        }
        const relativeY = (event.clientY - bounds.top) / bounds.height;
        sendCursor({
          componentId,
          relativeY: Math.min(Math.max(relativeY, 0), 1),
          scrollY: window.scrollY,
        });
      }}
      style={{
        ...blockStyle,
        position: "relative",
        cursor: isCommentMode ? "crosshair" : undefined,
        outline,
        outlineOffset: "2px",
        boxShadow: heatmapShadow,
        opacity: animatedStyle.opacity,
        transform: animatedStyle.transform,
        transitionProperty: animatedStyle.transitionProperty,
        transitionDuration: animatedStyle.transitionDuration,
        transitionDelay: animatedStyle.transitionDelay,
        transitionTimingFunction: animatedStyle.transitionTimingFunction,
      }}
    >
      {pins.map((pin) => (
        <button
          key={pin.id}
          type="button"
          title={pin.label}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedComponentId(componentId);
          }}
          className={`absolute z-10 inline-flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border text-[10px] font-semibold shadow-sm backdrop-blur ${
            pin.tone === "open"
              ? "border-amber-500/40 bg-amber-500/20 text-amber-950 dark:text-amber-100"
              : "border-emerald-500/40 bg-emerald-500/20 text-emerald-950 dark:text-emerald-100"
          }`}
          style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
          aria-label={pin.label}
        >
          •
        </button>
      ))}
      {pendingPin ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setSelectedComponentId(componentId);
            onCommentTarget(componentId, componentType, {
              x: pendingPin.x,
              y: pendingPin.y,
            });
          }}
          className="absolute z-10 inline-flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-dashed border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 text-[var(--color-primary)] shadow-sm backdrop-blur"
          style={{ left: `${pendingPin.x * 100}%`, top: `${pendingPin.y * 100}%` }}
          aria-label={`Add a page comment on ${componentType}`}
        >
          +
        </button>
      ) : null}
      {showHeatmap && typeof heat === "number" ? (
        <div className="pointer-events-none absolute right-2 top-2 rounded-full border border-border/70 bg-background/90 px-2 py-0.5 text-[10px] font-medium text-foreground shadow-sm">
          {Math.round(heat * 100)}%
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function withStylablePreview(
  componentName: string,
  definition: {
    render: (props: Record<string, unknown>) => React.ReactNode;
  } & Record<string, unknown>,
) {
  const originalRender = definition.render;

  return {
    ...definition,
    render: (props: Record<string, unknown>) => {
      const { visitorContext } = useContext(EditorDynamicPreviewContext);
      const renderProps = applyPreviewVariantProps(props, visitorContext);
      const componentId = typeof props.id === "string" && props.id
        ? props.id
        : `${componentName.toLowerCase()}-preview`;

      return (
        <StylablePreviewShell componentId={componentId} componentType={componentName} props={renderProps}>
          {originalRender(renderProps)}
        </StylablePreviewShell>
      );
    },
  };
}

type PreviewAnimationConfig = {
  entranceType?: string;
  durationMs?: number;
  delayMs?: number;
  easing?: string;
  scrollThreshold?: number;
};

function buildPreviewHiddenTransform(entranceType: string): string | undefined {
  switch (entranceType) {
    case "slide-up":
      return "translateY(30px)";
    case "slide-down":
      return "translateY(-30px)";
    case "slide-left":
      return "translateX(-30px)";
    case "slide-right":
      return "translateX(30px)";
    case "scale-up":
      return "scale(0.9)";
    case "zoom-in":
      return "scale(0.8)";
    case "pop-in":
      return "translateY(12px) scale(0.96)";
    case "rotate-in":
      return "rotate(-8deg) scale(0.96)";
    case "rotate-in-left":
      return "translateX(-24px) rotate(-10deg)";
    case "rotate-in-right":
      return "translateX(24px) rotate(10deg)";
    case "flip-x":
      return "perspective(1000px) rotateX(-24deg)";
    case "flip-y":
      return "perspective(1000px) rotateY(-24deg)";
    case "bounce-up":
      return "translateY(40px) scale(0.94)";
    case "skew-in":
      return "translateY(16px) skewY(-6deg)";
    default:
      return undefined;
  }
}

function extractPreviewAnimation(props: Record<string, unknown>): PreviewAnimationConfig | undefined {
  const dssAnimation = typeof props._dssStyles === "object" && props._dssStyles !== null
    && typeof (props._dssStyles as Record<string, unknown>).animation === "object"
    && (props._dssStyles as Record<string, unknown>).animation !== null
      ? (props._dssStyles as Record<string, unknown>).animation as Record<string, unknown>
      : null;

  const entranceType = typeof dssAnimation?.entranceType === "string"
    ? dssAnimation.entranceType
    : typeof dssAnimation?.entrance === "string"
      ? dssAnimation.entrance
    : typeof props.animationType === "string"
      ? props.animationType
      : undefined;

  if (!entranceType || entranceType === "none") {
    return undefined;
  }

  const durationMs = typeof dssAnimation?.durationMs === "number"
    ? dssAnimation.durationMs
    : typeof props.animationDuration === "string"
      ? Number(props.animationDuration)
      : 400;
  const delayMs = typeof dssAnimation?.delayMs === "number"
    ? dssAnimation.delayMs
    : typeof props.animationDelay === "string"
      ? Number(props.animationDelay)
      : 0;
  const scrollThreshold = typeof dssAnimation?.scrollThreshold === "number"
    ? dssAnimation.scrollThreshold
    : 0.15;
  const easing = typeof dssAnimation?.easing === "string"
    ? dssAnimation.easing
    : "ease";

  return {
    entranceType,
    durationMs: Number.isFinite(durationMs) ? durationMs : 400,
    delayMs: Number.isFinite(delayMs) ? delayMs : 0,
    easing,
    scrollThreshold: clampThreshold(scrollThreshold),
  };
}

function clampThreshold(value: number | undefined): number {
  const numeric = typeof value === "number" && Number.isFinite(value) ? value : 0.15;
  return Math.min(Math.max(numeric, 0.05), 0.9);
}

function buildPreviewAnimationStyle(
  animation: PreviewAnimationConfig | undefined,
  isVisible: boolean,
): {
  opacity?: number;
  transform?: string;
  transitionProperty?: string;
  transitionDuration?: string;
  transitionDelay?: string;
  transitionTimingFunction?: string;
} {
  if (!animation?.entranceType || animation.entranceType === "none") {
    return {};
  }

  return {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? undefined : buildPreviewHiddenTransform(animation.entranceType),
    transitionProperty: "opacity, transform",
    transitionDuration: `${animation.durationMs ?? 400}ms`,
    transitionDelay: `${animation.delayMs ?? 0}ms`,
    transitionTimingFunction: animation.easing ?? "ease",
  };
}
